// Reconcile the corpus against GET /api/notes/manifest, which lists
// { id, updatedAt } for every note the member owns regardless of view: rows the
// manifest no longer lists are dropped, rows whose updatedAt moved are fetched
// again by id, ids the corpus lacks are fetched only once the scope is complete
// (an incomplete scope's drain brings them), and nothing else is downloaded.
// docs/plans/2026-09-02-next-steps-e2e-encryption.md, section 6.7;
// hogc-one-api docs/next-steps-encryption-api.md section 3; #1548.
import { isQuotaError } from './idb';
import { noteScopeOf } from './schema';

// updatedAt arrives as an ISO string from JSON, but a clock that only changed
// its serialisation has not moved; compare instants when both parse.
export function sameInstant(a, b) {
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  if (!Number.isNaN(ta) && !Number.isNaN(tb)) return ta === tb;
  return String(a ?? '') === String(b ?? '');
}

export function createReconciler(store) {
  /**
   * @param manifest [{ id, updatedAt }] as the API returns it.
   * @param fetchById id => Promise<row | null>; null (a 404) means the note is gone.
   * @param scope the scope the Hub is showing; its `complete` decides whether new ids are fetched now.
   * @returns `{ ok: true, dropped, refetched, added, skipped }` or `{ ok: false, reason, error, ...counts }`
   */
  async function reconcile(memberId, manifest, { fetchById, scope }) {
    const counts = { dropped: 0, refetched: 0, added: 0, skipped: 0 };
    if (!store.hasStorage()) return { ok: false, reason: 'storage', ...counts };

    const records = await store.listRecords(memberId);
    const local = new Map(records.map(r => [r.id, r]));
    const listed = new Map((manifest || []).map(m => [String(m.id), m]));

    const drop = records.filter(r => !listed.has(r.id)).map(r => r.id);
    const changed = [...listed.values()]
      .map(m => String(m.id))
      .filter(
        id => local.has(id) && !sameInstant(local.get(id).updatedAt, listed.get(id).updatedAt),
      );
    const fresh = [...listed.keys()].filter(id => !local.has(id));
    const meta = await store.getMeta(memberId, scope);
    counts.skipped = meta.complete ? 0 : fresh.length;
    const toFetch = meta.complete ? [...changed, ...fresh] : changed;

    if (drop.length) {
      await store.deleteNotes(memberId, drop);
      counts.dropped += drop.length;
    }

    for (const id of toFetch) {
      let row;
      try {
        row = await fetchById(id);
      } catch (error) {
        return { ok: false, reason: 'fetch', error, ...counts };
      }
      if (!row) {
        // Listed a moment ago, gone now: a deletion that beat the manifest.
        if (local.has(id)) {
          await store.deleteNotes(memberId, [id]);
          counts.dropped += 1;
        }
        continue;
      }
      const rowScope = noteScopeOf(row, scope);
      try {
        await store.putNote(memberId, row, rowScope);
      } catch (error) {
        // A row that cannot be written leaves a corpus that claims to be
        // complete while holding a stale row: the flag comes off, search stays
        // disabled, and the next open re-drains.
        await store.clearComplete(memberId, rowScope).catch(() => {});
        await store.clearComplete(memberId, scope).catch(() => {});
        return { ok: false, reason: isQuotaError(error) ? 'quota' : 'storage', error, ...counts };
      }
      if (local.has(id)) counts.refetched += 1;
      else counts.added += 1;
    }
    return { ok: true, ...counts };
  }

  return { reconcile };
}
